/** Fetch private figures directly; never place them in conversation storage. */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import UCPayrollPreview from "../../UCPayrollPreview";
import { useAuth } from "../../../context/AuthContext";

const ROOT = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const client = axios.create({ withCredentials: true, timeout: 30000 });
const ID = /^[a-f0-9]{64}$/;

function Report({ reference }) {
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [retry, setRetry] = useState(0);
  const textRef = useRef(null);
  const alive = useRef(true);
  const url = `${ROOT}/api/uc/reports/${reference.id}`;
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setReport(null); setError(""); setMessage("");
    client.get(url, { signal: controller.signal, headers: { "Cache-Control": "no-store" } })
      .then(({ data }) => {
        if (data?.id !== reference.id || data?.report_sha256 !== reference.report_sha256
            || data?.status !== "draft" || data?.ready_to_submit !== false
            || typeof data?.copy_text !== "string" || data.copy_text.length > 25000
            || typeof data?.notice !== "string") throw new Error("Report mismatch");
        if (active) setReport(data);
      }).catch(() => {
        if (active) setError("The saved report could not be verified. Check your sign-in and try again, or ask Karen for the report again.");
      });
    return () => { active = false; controller.abort(); };
  }, [url, reference.id, reference.report_sha256, retry]);

  const copy = async () => {
    if (!report) return;
    try {
      await navigator.clipboard.writeText(report.copy_text);
      if (alive.current) setMessage("Draft copied. It still needs validation before submission.");
    } catch (_) {
      if (!alive.current) return;
      textRef.current?.focus(); textRef.current?.select();
      setMessage("Automatic copy is unavailable. The text is selected; use your device's Copy command.");
    }
  };

  if (error) return <div role="alert"><p>{error}</p>
    <button type="button" className="karen-panel-button" onClick={() => setRetry((n) => n + 1)}>Try opening again</button></div>;
  if (!report) return <p role="status">Opening your saved UC report…</p>;
  return <section data-testid="karen-uc-report">
    <p role="note"><strong>{report.notice}</strong></p>
    <p>Assessment period: {report.period_start} to {report.period_end}. Version {report.revision}.</p>
    <div className="karen-mail-toolbar">
      <a className="karen-panel-button" href={`${url}/download`} target="_blank" rel="noopener noreferrer"
        data-testid="uc-download">Download draft PDF</a>
      <button type="button" className="karen-panel-button" onClick={copy} data-testid="uc-copy">Copy draft figures</button>
    </div>
    <p role="status" aria-live="polite">{message}</p>
    <label htmlFor="uc-copy-text">Report text</label>
    <textarea id="uc-copy-text" ref={textRef} readOnly value={report.copy_text} rows={14}
      style={{ width: "100%", boxSizing: "border-box", padding: "12px", font: "inherit",
        background: "#0c1422", color: "#f1f5f9", border: "1px solid #64748b" }} />
    <p>Downloading or copying does not submit anything to Universal Credit.</p>
  </section>;
}

export default function UCReportAnswer({ presentation }) {
  const { user, loading } = useAuth();
  const owner = String(user?.id || user?._id || "");
  const originalOwner = useRef(null);
  if (!loading && owner && originalOwner.current === null) originalOwner.current = owner;
  const ref = presentation?.data;
  if (loading || !owner) return <p role="status">Sign in to open your UC report.</p>;
  if (owner !== originalOwner.current || ref?.owner_id !== owner
      || !["adi", "pdi", "admin"].includes(user?.role)) {
    return <p role="alert">Your account changed or cannot access this report. Ask Karen for the report again.</p>;
  }
  if (ref?.kind === "payroll_search" && Number.isSafeInteger(ref.setup_revision) && ref.setup_revision >= 0
      && ID.test(ref.source_snapshot_sha256 || "")) {
    return <UCPayrollPreview key={`${owner}:${ref.setup_revision}:${ref.source_snapshot_sha256}`}
      setupRevision={ref.setup_revision} sourceSnapshot={ref.source_snapshot_sha256} autoSearch />;
  }
  if (!ID.test(ref?.id || "") || !ID.test(ref?.report_sha256 || "")) {
    return <p role="alert">Ask Karen for the UC report again; its reference is missing.</p>;
  }
  return <Report key={`${owner}:${ref.id}:${ref.report_sha256}`} reference={ref} />;
}

